import libCurve from '@/util/Curve'
import { compareTowNum, isSamePart } from '@/util/commonFuncs.js'
import { getCounterAxisClearance } from '@/util/counterLimit'
import {
  checkOnePlank,
  dealBigPlank,
  dealPlankPoly,
  getAdjacentEdgeRequiredGap,
  getNearestPartsInRange,
  getPlateKnifeDiameter,
} from '@/util/dealPaibanData.js'
import { getSawKnife } from '@/util/LayoutFuncs.js'
import { compareNumbers } from '@/util/graphCalc'
import {
  getPathMinMaxPoint,
  isLShapeSurplusBigPlank,
  isSpecialShapeSurplusBigPlank,
} from '@/util/plankCommonFuncs'
import { polyContain } from '@/util/polygonRelation'

const KEYBOARD_DIR_TO_EDGE = {
  up: 'top',
  down: 'bottom',
  left: 'left',
  right: 'right',
}

/**
 * 与 drawPart 一致：四边中心条带净空、键盘步长、修边大板 shrink 边界、嵌套孔洞判定与四向距离线绘制。
 * 大板边距优先 bigPlankShrinkPolygon；异形大板仅用轮廓射线净空，不用外接矩形放大。
 * 外部异形板之间外接矩形重叠 / 落入异形外接矩形空白区时，参与嵌套组不绘制距离。
 * 嵌套外层板（带孔母板）绘制四向距离；键盘移动时孔内子板不动，外层板遇碰撞则步长移不满。
 * 邻板方向键盘移板预留间隙与 checkOnePlank 一致（锯片/异形刀缝 + fedm），间隙展示仍为几何净空。
 * 使用组件需提供：activePlank、bigpart、ctx、ncSetting、scalePercent、tranlateSizeFromScale、isInPolygon、sliceDrawData（可选）
 */
export default {
  methods: {
    getKeyboardStepCap() {
      const raw = Number(this.$props.moveStep)
      if (this.$props.isShowMoveStep && !Number.isNaN(raw) && raw > 0) {
        return raw
      }
      return 20
    },

    resolveLiveActivePart(bigpart) {
      if (!this.activePlank || !bigpart?.parts?.length) return this.activePlank
      const live = bigpart.parts.find((p) => isSamePart(p, this.activePlank))
      return live ?? this.activePlank
    },

    findBigpartForEdgeOverlay() {
      if (!this.activePlank) return null
      const sk = this.activePlank.stockKey
      if (Array.isArray(this.sliceDrawData)) {
        const fromSlice = this.sliceDrawData.find((v) => v.stockKey === sk)
        if (fromSlice) return fromSlice
      }
      if (Array.isArray(this.drawData?.data)) {
        const fromMain = this.drawData.data.find((v) => v.stockKey === sk)
        if (fromMain) return fromMain
      }
      if (this.bigpart && this.bigpart.stockKey === sk) return this.bigpart
      if (Array.isArray(this.drawData)) {
        for (let di = 0; di < this.drawData.length; di++) {
          const row = this.drawData[di]?.data
          if (!Array.isArray(row)) continue
          const hit = row.find((v) => v.stockKey === sk)
          if (hit) return hit
        }
      }
      return null
    },

    getAxisClearanceFromMinMax(direction, activeArr, minX, maxX, minY, maxY) {
      if (
        !Number.isFinite(minX) ||
        !Number.isFinite(maxX) ||
        !Number.isFinite(minY) ||
        !Number.isFinite(maxY)
      ) {
        return Infinity
      }
      const { activeLeft, activeRight, activeTop, activeBottom } = activeArr
      let d
      if (direction === 'up') {
        d = activeTop - minY
      } else if (direction === 'down') {
        d = maxY - activeBottom
      } else if (direction === 'left') {
        d = activeLeft - minX
      } else if (direction === 'right') {
        d = maxX - activeRight
      } else {
        return Infinity
      }
      if (!Number.isFinite(d)) return Infinity
      if (compareTowNum(d, 0, '>=')) return d
      return 0
    },

    /** 多边形轮廓在中心条带方向上的净空（含斜边，与 getHolePolygonAxisClearance 一致） */
    getPolygonStripClearance(direction, activeArr, polygon, options = {}) {
      if (!polygon?.length) return Infinity
      const pts = polygon.map((p) => ({
        X: Number(p.X ?? p.x),
        Y: Number(p.Y ?? p.y),
      }))
      const d = this.getHolePolygonAxisClearance(
        direction,
        activeArr,
        pts,
        options
      )
      return Number.isFinite(d) && d !== Infinity ? d : Infinity
    },

    getPartPolygonStripClearance(direction, activeArr, part) {
      const poly = part?.polyArr?.[0]
      if (!poly?.length) return Infinity
      return this.getPolygonStripClearance(direction, activeArr, poly, {
        centerStripOnly: true,
      })
    },

    getPointToSegmentDistanceSq(px, py, ax, ay, bx, by) {
      const dx = bx - ax
      const dy = by - ay
      if (Math.abs(dx) < 1e-12 && Math.abs(dy) < 1e-12) {
        const ox = px - ax
        const oy = py - ay
        return ox * ox + oy * oy
      }
      let t = ((px - ax) * dx + (py - ay) * dy) / (dx * dx + dy * dy)
      t = Math.max(0, Math.min(1, t))
      const cx = ax + t * dx
      const cy = ay + t * dy
      const ox = px - cx
      const oy = py - cy
      return ox * ox + oy * oy
    },

    isPointNearPolygonBoundary(point, polygonPoints, eps = 0.5) {
      if (!polygonPoints?.length) return false
      const epsSq = eps * eps
      const px = point.x
      const py = point.y
      const n = polygonPoints.length
      for (let i = 0; i < n; ++i) {
        const a = polygonPoints[i]
        const b = polygonPoints[(i + 1) % n]
        const distSq = this.getPointToSegmentDistanceSq(
          px,
          py,
          a.x,
          a.y,
          b.x,
          b.y
        )
        if (distSq <= epsSq) return true
      }
      return false
    },

    isPointOnOrInsidePolygon(point, polygonPoints, eps = 0.5) {
      if (!polygonPoints?.length) return false
      if (this.isInPolygon(point, polygonPoints)) return true
      return this.isPointNearPolygonBoundary(point, polygonPoints, eps)
    },

    getShrinkPolygonAxisBBoxClearance(shrinkPoly, direction, activeArr) {
      if (!shrinkPoly?.length) return Infinity
      const shrinkPts = shrinkPoly.map((p) => ({
        x: Number(p.x ?? p.X),
        y: Number(p.y ?? p.y),
      }))
      const [minX, minY, maxX, maxY] = getPathMinMaxPoint(shrinkPts)
      if (
        !Number.isFinite(minX) ||
        !Number.isFinite(maxX) ||
        !Number.isFinite(minY) ||
        !Number.isFinite(maxY)
      ) {
        return Infinity
      }
      return this.getAxisClearanceFromMinMax(
        direction,
        activeArr,
        minX,
        maxX,
        minY,
        maxY
      )
    },

    mergePolygonAndBBoxClearance(polygonClearance, bboxClearance) {
      if (Number.isFinite(polygonClearance) && polygonClearance !== Infinity) {
        if (Number.isFinite(bboxClearance) && bboxClearance !== Infinity) {
          return Math.min(polygonClearance, bboxClearance)
        }
        return polygonClearance
      }
      if (Number.isFinite(bboxClearance) && bboxClearance !== Infinity) {
        return bboxClearance
      }
      return Infinity
    },

    /** 外接矩形在中心条带方向上的净空（相邻/overlap 时按排版间隙 0 处理） */
    getPartAabbStripClearance(direction, activeArr, part, eps = 0.5) {
      const partLeft = part.startX
      const partRight = part.startX + part.rect.width
      const partTop = part.startY
      const partBottom = part.startY + part.rect.height
      const { activeLeft, activeRight, activeTop, activeBottom } = activeArr

      if (direction === 'left') {
        if (partRight <= activeLeft + eps) {
          const d = activeLeft - partRight
          return compareTowNum(d, 0, '>=') ? d : 0
        }
        return Infinity
      }
      if (direction === 'right') {
        if (partLeft >= activeRight - eps) {
          const d = partLeft - activeRight
          return compareTowNum(d, 0, '>=') ? d : 0
        }
        return Infinity
      }
      if (direction === 'up') {
        if (partBottom <= activeTop + eps) {
          const d = activeTop - partBottom
          return compareTowNum(d, 0, '>=') ? d : 0
        }
        return Infinity
      }
      if (direction === 'down') {
        if (partTop >= activeBottom - eps) {
          const d = partTop - activeBottom
          return compareTowNum(d, 0, '>=') ? d : 0
        }
        return Infinity
      }
      return Infinity
    },

    /** 中心条带方向上，比较板件是否位于选中板件该侧的障碍物 */
    isPartBlockingInDirection(direction, activeArr, partArr, eps = 0.5) {
      const { activeLeft, activeRight, activeTop, activeBottom } = activeArr
      const { partLeft, partRight, partTop, partBottom } = partArr
      const hasVerticalOverlap =
        partTop < activeBottom - eps && partBottom > activeTop + eps
      const hasHorizontalOverlap =
        partLeft < activeRight - eps && partRight > activeLeft + eps

      if (direction === 'left') {
        return hasVerticalOverlap && partRight <= activeLeft + eps
      }
      if (direction === 'right') {
        return hasVerticalOverlap && partLeft >= activeRight - eps
      }
      if (direction === 'up') {
        return hasHorizontalOverlap && partBottom <= activeTop + eps
      }
      if (direction === 'down') {
        return hasHorizontalOverlap && partTop >= activeBottom - eps
      }
      return false
    },

    /** 选中板件轮廓（polyArr 优先，与 canvas 绘制一致） */
    getActiveOutlineArr(ap) {
      const poly = ap?.polyArr?.[0]
      if (poly?.length >= 3) {
        const pts = poly.map((p) => ({
          x: Number(p.X ?? p.x),
          y: Number(p.Y ?? p.y),
        }))
        const [minX, minY, maxX, maxY] = getPathMinMaxPoint(pts)
        if (
          Number.isFinite(minX) &&
          Number.isFinite(maxX) &&
          Number.isFinite(minY) &&
          Number.isFinite(maxY)
        ) {
          return {
            activeLeft: minX,
            activeRight: maxX,
            activeTop: minY,
            activeBottom: maxY,
          }
        }
      }
      const left = ap.startX
      const top = ap.startY
      const w = ap.rect.width
      const h = ap.rect.height
      return {
        activeLeft: left,
        activeRight: left + w,
        activeTop: top,
        activeBottom: top + h,
      }
    },

    getBigPlankVisualPath(bigpart) {
      const path = bigpart?.path
      if (!Array.isArray(path) || !path.length) return null
      return path.map((p) => ({
        X: Number(p.x ?? p.X),
        Y: Number(p.y ?? p.y),
      }))
    },

    /** L 形 / 特殊异形余料等：不能用 shrink 外接矩形当净空 */
    isIrregularBigPlankOutline(bigpart) {
      if (!bigpart) return false
      if (isLShapeSurplusBigPlank(bigpart)) return true
      if (isSpecialShapeSurplusBigPlank(bigpart)) return true
      const path = bigpart.path
      return Array.isArray(path) && path.length >= 3
    },

    getBigPlankAxisClearance(bigpart, direction, activeArr) {
      if (!bigpart) return Infinity
      if (
        !Array.isArray(bigpart.bigPlankShrinkPolygon) ||
        !bigpart.bigPlankShrinkPolygon.length
      ) {
        try {
          dealBigPlank(bigpart)
        } catch (_e) {
          /* 与 dealCurrentPointDistance 一致 */
        }
      }
      const isIrregular = this.isIrregularBigPlankOutline(bigpart)
      const shrinkPoly = bigpart.bigPlankShrinkPolygon
      const irregularStripOptions = { centerStripOnly: true }
      if (Array.isArray(shrinkPoly) && shrinkPoly.length) {
        const shrinkClearance = this.getPolygonStripClearance(
          direction,
          activeArr,
          shrinkPoly,
          isIrregular ? irregularStripOptions : undefined
        )
        if (isIrregular) {
          const bboxClearance = this.getShrinkPolygonAxisBBoxClearance(
            shrinkPoly,
            direction,
            activeArr
          )
          if (
            Number.isFinite(shrinkClearance) &&
            shrinkClearance !== Infinity
          ) {
            return this.mergePolygonAndBBoxClearance(
              shrinkClearance,
              bboxClearance
            )
          }
          const visualPath = this.getBigPlankVisualPath(bigpart)
          if (visualPath?.length) {
            const visualClearance = this.getPolygonStripClearance(
              direction,
              activeArr,
              visualPath,
              irregularStripOptions
            )
            if (
              Number.isFinite(visualClearance) &&
              visualClearance !== Infinity
            ) {
              return this.mergePolygonAndBBoxClearance(
                visualClearance,
                bboxClearance
              )
            }
          }
          return bboxClearance
        }

        const bboxClearance = this.getShrinkPolygonAxisBBoxClearance(
          shrinkPoly,
          direction,
          activeArr
        )
        if (Number.isFinite(shrinkClearance) && shrinkClearance !== Infinity) {
          return this.mergePolygonAndBBoxClearance(
            shrinkClearance,
            bboxClearance
          )
        }
        if (Number.isFinite(bboxClearance) && bboxClearance !== Infinity) {
          return bboxClearance
        }
      }
      if (isIrregular) {
        const visualPath = this.getBigPlankVisualPath(bigpart)
        if (visualPath?.length) {
          const visualClearance = this.getPolygonStripClearance(
            direction,
            activeArr,
            visualPath,
            irregularStripOptions
          )
          if (
            Number.isFinite(visualClearance) &&
            visualClearance !== Infinity
          ) {
            return visualClearance
          }
        }
        if (Array.isArray(shrinkPoly) && shrinkPoly.length) {
          return this.getShrinkPolygonAxisBBoxClearance(
            shrinkPoly,
            direction,
            activeArr
          )
        }
        return Infinity
      }
      const path = bigpart?.bigPlank?.[0]
      if (!path?.length) return Infinity
      let minX = Infinity
      let maxX = -Infinity
      let minY = Infinity
      let maxY = -Infinity
      for (let i = 0; i < path.length; ++i) {
        const p = path[i]
        const x = Number(p.X ?? p.x)
        const y = Number(p.Y ?? p.y)
        if (Number.isNaN(x) || Number.isNaN(y)) continue
        minX = Math.min(minX, x)
        maxX = Math.max(maxX, x)
        minY = Math.min(minY, y)
        maxY = Math.max(maxY, y)
      }
      return this.getAxisClearanceFromMinMax(
        direction,
        activeArr,
        minX,
        maxX,
        minY,
        maxY
      )
    },

    getHolePolygonAbs(parent, holeIndex) {
      if (holeIndex < 1) return null
      const sx = Number(parent.startX) || 0
      const sy = Number(parent.startY) || 0

      const poly = parent.polyArr?.[holeIndex]
      if (poly?.length >= 3) {
        return poly.map((p) => ({
          X: Number(p.X ?? p.x),
          Y: Number(p.Y ?? p.y),
        }))
      }

      const hole = parent.path?.[holeIndex]
      if (!hole?.length) return null

      const hasArc = hole.some(
        (p) => p.b !== undefined && p.b !== null && Math.abs(Number(p.b)) > 1e-6
      )
      if (hasArc || hole.length < 3) {
        const curve = libCurve.newCurve()
        curve.points = hole.map((p) => ({
          x: Number(p.x ?? p.X),
          y: Number(p.y ?? p.Y),
          b: p.b,
        }))
        const tess = curve.getPolygon(true)
        if (tess?.length >= 3) {
          return tess.map((p) => ({
            X: Number((p.x + sx).toFixed(3)),
            Y: Number((p.y + sy).toFixed(3)),
          }))
        }
      }

      if (hole.length >= 3) {
        return hole.map((p) => ({
          X: Number(p.x ?? p.X) + sx,
          Y: Number(p.y ?? p.Y) + sy,
        }))
      }
      return null
    },

    isLikelySamePlank(part1, part2) {
      if (!part1 || !part2) return false
      if (isSamePart(part1, part2)) return true
      const id1 = part1.plankID ?? part1.plankId
      const id2 = part2.plankID ?? part2.plankId
      return id1 != null && id2 != null && id1 === id2
    },

    hasPenetratingHoles(part) {
      if (!part) return false
      if ((part.path?.length || 0) > 1) return true
      if ((part.polyArr?.length || 0) > 1) return true
      const curveHoles = part.curveHoles || []
      for (let i = 0; i < curveHoles.length; i++) {
        if (compareNumbers(curveHoles[i].deep, part.thick)) return true
      }
      return Boolean(part._curveHolesPoly?.length)
    },

    /** 带 path / polyArr 外轮廓的异形板（外部嵌套判定用，与孔洞母板判定独立） */
    isExternalIrregularPlank(part) {
      const outer = part?.path?.[0]
      if (outer?.length >= 3) return true
      const poly = part?.polyArr?.[0]
      return Boolean(poly?.length > 4)
    },

    getPartAabbFromRect(part) {
      return {
        partLeft: part.startX,
        partRight: part.startX + part.rect.width,
        partTop: part.startY,
        partBottom: part.startY + part.rect.height,
      }
    },

    /** 外接矩形中心与四角，用于判定是否落入异形外接矩形空白区 */
    getPartRectSamplePoints(part) {
      const { partLeft, partRight, partTop, partBottom } =
        this.getPartAabbFromRect(part)
      return [
        { x: (partLeft + partRight) / 2, y: (partTop + partBottom) / 2 },
        { x: partLeft, y: partTop },
        { x: partRight, y: partTop },
        { x: partLeft, y: partBottom },
        { x: partRight, y: partBottom },
      ]
    },

    getIrregularOuterPolygonPoints(irregular) {
      const poly = irregular?.polyArr?.[0]
      if (!poly?.length) return null
      return poly.map((p) => ({
        x: Number(p.X ?? p.x),
        y: Number(p.Y ?? p.y),
      }))
    },

    isPointOnIrregularOuterMaterial(irregular, x, y) {
      const polygonPoints = this.getIrregularOuterPolygonPoints(irregular)
      if (!polygonPoints?.length) return false
      return this.isInPolygon({ x, y }, polygonPoints)
    },

    isPointInAnyHoleOf(outer, x, y) {
      const pathLen = outer.path?.length || 0
      for (let hi = 1; hi < pathLen; hi++) {
        const hp = this.getHolePolygonAbs(outer, hi)
        if (!hp?.length) continue
        const polygonPoints = hp.map((p) => ({
          x: Number(p.X ?? p.x),
          y: Number(p.Y ?? p.y),
        }))
        if (this.isInPolygon({ x, y }, polygonPoints)) return true
      }
      const curvePenCount =
        outer._curveHolesPoly?.length ||
        (outer.curveHoles || []).filter((ch) =>
          compareNumbers(ch.deep, outer.thick)
        ).length
      for (let pi = 0; pi < curvePenCount; pi++) {
        const hp = this.getCurveHolePolygonAbs(outer, pi)
        if (!hp?.length) continue
        const polygonPoints = hp.map((p) => ({
          x: Number(p.X ?? p.x),
          y: Number(p.Y ?? p.y),
        }))
        if (this.isInPolygon({ x, y }, polygonPoints)) return true
      }
      return false
    },

    /** 单点是否落在异形外接矩形内、且不在其实体轮廓与孔洞内 */
    isPointInIrregularAabbBlank(irregular, x, y, eps = 0.5) {
      const aabb = this.getPartAabbFromRect(irregular)
      if (
        x < aabb.partLeft - eps ||
        x > aabb.partRight + eps ||
        y < aabb.partTop - eps ||
        y > aabb.partBottom + eps
      ) {
        return false
      }
      if (this.isPointOnIrregularOuterMaterial(irregular, x, y)) return false
      if (this.isPointInAnyHoleOf(irregular, x, y)) return false
      return true
    },

    isAabbOverlap(aabbA, aabbB, eps = 0.5) {
      return (
        aabbA.partLeft < aabbB.partRight - eps &&
        aabbA.partRight > aabbB.partLeft + eps &&
        aabbA.partTop < aabbB.partBottom - eps &&
        aabbA.partBottom > aabbB.partTop + eps
      )
    },

    isPartCenterInAabb(part, aabb, eps = 0.5) {
      const cx = part.startX + part.rect.width / 2
      const cy = part.startY + part.rect.height / 2
      return (
        cx >= aabb.partLeft - eps &&
        cx <= aabb.partRight + eps &&
        cy >= aabb.partTop - eps &&
        cy <= aabb.partBottom + eps
      )
    },

    isPartCenterOnIrregularOuterMaterial(irregular, other) {
      const cx = other.startX + other.rect.width / 2
      const cy = other.startY + other.rect.height / 2
      return this.isPointOnIrregularOuterMaterial(irregular, cx, cy)
    },

    isPointInAabb(x, y, aabb, eps = 0.5) {
      return (
        x >= aabb.partLeft - eps &&
        x <= aabb.partRight + eps &&
        y >= aabb.partTop - eps &&
        y <= aabb.partBottom + eps
      )
    },

    getAabbOverlapRect(aabbA, aabbB) {
      const partLeft = Math.max(aabbA.partLeft, aabbB.partLeft)
      const partRight = Math.min(aabbA.partRight, aabbB.partRight)
      const partTop = Math.max(aabbA.partTop, aabbB.partTop)
      const partBottom = Math.min(aabbA.partBottom, aabbB.partBottom)
      if (partLeft >= partRight || partTop >= partBottom) return null
      return { partLeft, partRight, partTop, partBottom }
    },

    /** 其它板外接矩形与异形板外接矩形重叠，且重叠区内有点落入异形外接矩形空白区 */
    isPartInIrregularAabbBlank(irregular, other) {
      if (!this.isExternalIrregularPlank(irregular)) return false
      if (this.isLikelySamePlank(irregular, other)) return false
      const aabbIrreg = this.getPartAabbFromRect(irregular)
      const aabbOther = this.getPartAabbFromRect(other)
      if (!this.isAabbOverlap(aabbIrreg, aabbOther)) return false
      const overlap = this.getAabbOverlapRect(aabbIrreg, aabbOther)
      if (!overlap) return false
      const samplePoints = this.getPartRectSamplePoints(other)
      for (let i = 0; i < samplePoints.length; ++i) {
        const p = samplePoints[i]
        if (!this.isPointInAabb(p.x, p.y, overlap)) continue
        if (this.isPointInIrregularAabbBlank(irregular, p.x, p.y)) {
          return true
        }
      }
      return false
    },

    hasExternalIrregularNestRelation(partA, partB) {
      if (!partA || !partB || this.isLikelySamePlank(partA, partB)) {
        return false
      }
      if (this.isPartNestedInAnyHoleOf(partA, partB)) return false
      if (this.isPartNestedInAnyHoleOf(partB, partA)) return false

      const aabbA = this.getPartAabbFromRect(partA)
      const aabbB = this.getPartAabbFromRect(partB)
      const aIrreg = this.isExternalIrregularPlank(partA)
      const bIrreg = this.isExternalIrregularPlank(partB)

      if (aIrreg && bIrreg && this.isAabbOverlap(aabbA, aabbB)) return true
      if (aIrreg && this.isPartInIrregularAabbBlank(partA, partB)) return true
      if (bIrreg && this.isPartInIrregularAabbBlank(partB, partA)) return true
      return false
    },

    /** 与外部异形板嵌套（外接矩形重叠 / 落入外接矩形空白）同组的板件不绘制四向距离 */
    isPartInExternalIrregularNestCluster(ap, filteredParts) {
      if (!ap || !filteredParts?.length) return false
      if (this.isPartInNestHoleInterior(ap, filteredParts)) return false

      const n = filteredParts.length
      const parent = new Array(n)
      for (let i = 0; i < n; ++i) parent[i] = i

      function find(x) {
        if (parent[x] !== x) parent[x] = find(parent[x])
        return parent[x]
      }

      function unite(a, b) {
        const ra = find(a)
        const rb = find(b)
        if (ra !== rb) parent[ra] = rb
      }

      for (let i = 0; i < n; ++i) {
        for (let j = i + 1; j < n; ++j) {
          if (
            this.hasExternalIrregularNestRelation(
              filteredParts[i],
              filteredParts[j]
            )
          ) {
            unite(i, j)
          }
        }
      }

      let apIdx = -1
      for (let i = 0; i < n; ++i) {
        if (isSamePart(filteredParts[i], ap)) {
          apIdx = i
          break
        }
      }
      if (apIdx < 0) return false

      const root = find(apIdx)
      let clusterSize = 0
      for (let i = 0; i < n; ++i) {
        if (find(i) === root) clusterSize++
      }
      return clusterSize >= 2
    },

    /** 嵌套在他人孔洞内的子板不绘制四向距离 */
    isPartInNestHoleInterior(ap, filteredParts) {
      if (!ap) return false
      const nest = this.findActiveNestHoleContext(ap, filteredParts)
      if (!nest || this.isLikelySamePlank(ap, nest.parent)) return false
      return true
    },

    /** 带孔母板（非孔内子板）：四向对大板外轮廓 */
    isOuterNestShell(ap, filteredParts) {
      if (!ap || !this.hasPenetratingHoles(ap)) return false
      return !this.isPartInNestHoleInterior(ap, filteredParts)
    },

    getCurveHolePolygonAbs(parent, penetratingIdx) {
      const cached = parent._curveHolesPoly?.[penetratingIdx]
      if (cached?.length >= 3) return cached
      let idx = 0
      const list = parent.curveHoles || []
      const sx = Number(parent.startX) || 0
      const sy = Number(parent.startY) || 0
      for (let ci = 0; ci < list.length; ci++) {
        const ch = list[ci]
        if (!compareNumbers(ch.deep, parent.thick)) continue
        if (idx === penetratingIdx && ch.path?.length) {
          return ch.path.map((p) => ({
            X: Number(p.x ?? p.X) + sx,
            Y: Number(p.y ?? p.Y) + sy,
          }))
        }
        idx++
      }
      return null
    },

    getPolygonAabb(holePoly) {
      if (!holePoly?.length) return null
      let minX = Infinity
      let maxX = -Infinity
      let minY = Infinity
      let maxY = -Infinity
      for (let i = 0; i < holePoly.length; ++i) {
        const p = holePoly[i]
        const x = Number(p.X ?? p.x)
        const y = Number(p.Y ?? p.y)
        if (Number.isNaN(x) || Number.isNaN(y)) continue
        minX = Math.min(minX, x)
        maxX = Math.max(maxX, x)
        minY = Math.min(minY, y)
        maxY = Math.max(maxY, y)
      }
      if (!Number.isFinite(minX)) return null
      return { minX, maxX, minY, maxY }
    },

    isRectCenterInHolePolyDirect(holePoly, part) {
      if (!holePoly?.length || !part?.rect) return false
      const cx = part.startX + part.rect.width / 2
      const cy = part.startY + part.rect.height / 2
      const polygonPoints = holePoly.map((p) => ({
        x: Number(p.X ?? p.x),
        y: Number(p.Y ?? p.y),
      }))
      return this.isInPolygon({ x: cx, y: cy }, polygonPoints)
    },

    isPartInsideHolePolygon(holePoly, part) {
      if (!holePoly?.length) return false
      if (part?.polyArr?.[0]) {
        if (polyContain([holePoly], [part.polyArr[0]])) {
          return true
        }
      }
      if (!part?.rect) return false
      const hb = this.getPolygonAabb(holePoly)
      if (!hb) return this.isRectCenterInHolePolyDirect(holePoly, part)
      const holeW = hb.maxX - hb.minX
      const holeH = hb.maxY - hb.minY
      const pw = part.rect.width
      const ph = part.rect.height
      const eps = 2
      // 两向外接均大于孔洞 AABB：外层板，不以中心点判入孔（避免心落在孔内子板开孔上误判）
      if (pw > holeW + eps && ph > holeH + eps) {
        return false
      }
      // 仅单边超出：异形孔 AABB 偏紧，仍用中心点判定孔内子板
      return this.isRectCenterInHolePolyDirect(holePoly, part)
    },

    getHolePolygonAxisClearance(direction, activeArr, holePoly, options = {}) {
      const { centerStripOnly = false } = options
      const eps = 1e-6
      if (!holePoly?.length || holePoly.length < 3) return Infinity
      const pts = holePoly.map((p) => ({
        x: Number(p.X ?? p.x),
        y: Number(p.Y ?? p.y),
      }))
      const { activeLeft, activeRight, activeTop, activeBottom } = activeArr
      const cx = (activeLeft + activeRight) / 2
      const cy = (activeTop + activeBottom) / 2

      function intersectHorizontal(y0, a, b) {
        const dy = b.y - a.y
        if (Math.abs(dy) < eps) return null
        const t = (y0 - a.y) / dy
        if (t < -eps || t > 1 + eps) return null
        return a.x + t * (b.x - a.x)
      }

      function intersectVertical(x0, a, b) {
        const dx = b.x - a.x
        if (Math.abs(dx) < eps) return null
        const t = (x0 - a.x) / dx
        if (t < -eps || t > 1 + eps) return null
        return a.y + t * (b.y - a.y)
      }

      const n = pts.length
      let d = Infinity
      // 中心条带与距离线锚点一致：仅沿 cx/cy 射线；孔洞边界仍用四角+中心，避免宽板角点越界
      const ySamples =
        direction === 'left' || direction === 'right'
          ? centerStripOnly
            ? [cy]
            : [activeTop, activeBottom, cy]
          : []
      const xSamples =
        direction === 'up' || direction === 'down'
          ? centerStripOnly
            ? [cx]
            : [activeLeft, activeRight, cx]
          : []

      function appendSampleGap(sampleGap) {
        if (Number.isFinite(sampleGap) && compareTowNum(sampleGap, 0, '>=')) {
          d = Math.min(d, sampleGap)
        }
      }

      const resolveFlushGapWhenRayMissed = (anchorPoint, probePoint) => {
        const onOrNear = this.isPointOnOrInsidePolygon(
          anchorPoint,
          pts,
          boundaryEps
        )
        if (onOrNear) {
          return this.isInPolygon(probePoint, pts) ? Infinity : 0
        }
        if (
          !this.isInPolygon(probePoint, pts) &&
          !this.isPointNearPolygonBoundary(probePoint, pts, boundaryEps)
        ) {
          return 0
        }
        return Infinity
      }

      const boundaryEps = 0.5
      const probeStep = 1

      if (direction === 'left') {
        for (let si = 0; si < ySamples.length; ++si) {
          const y0 = ySamples[si]
          let sampleGap = Infinity
          for (let i = 0; i < n; ++i) {
            const xi = intersectHorizontal(y0, pts[i], pts[(i + 1) % n])
            if (xi == null) continue
            if (xi < activeLeft - eps) {
              sampleGap = Math.min(sampleGap, activeLeft - xi)
            } else if (Math.abs(xi - activeLeft) <= boundaryEps) {
              sampleGap = Math.min(sampleGap, 0)
            }
          }
          if (!Number.isFinite(sampleGap)) {
            sampleGap = resolveFlushGapWhenRayMissed(
              { x: activeLeft, y: y0 },
              { x: activeLeft - probeStep, y: y0 }
            )
          }
          appendSampleGap(sampleGap)
        }
      } else if (direction === 'right') {
        for (let si = 0; si < ySamples.length; ++si) {
          const y0 = ySamples[si]
          let sampleGap = Infinity
          for (let i = 0; i < n; ++i) {
            const xi = intersectHorizontal(y0, pts[i], pts[(i + 1) % n])
            if (xi == null) continue
            if (xi > activeRight + eps) {
              sampleGap = Math.min(sampleGap, xi - activeRight)
            } else if (Math.abs(xi - activeRight) <= boundaryEps) {
              sampleGap = Math.min(sampleGap, 0)
            }
          }
          if (!Number.isFinite(sampleGap)) {
            sampleGap = resolveFlushGapWhenRayMissed(
              { x: activeRight, y: y0 },
              { x: activeRight + probeStep, y: y0 }
            )
          }
          appendSampleGap(sampleGap)
        }
      } else if (direction === 'up') {
        for (let si = 0; si < xSamples.length; ++si) {
          const x0 = xSamples[si]
          let sampleGap = Infinity
          for (let i = 0; i < n; ++i) {
            const yi = intersectVertical(x0, pts[i], pts[(i + 1) % n])
            if (yi == null) continue
            if (yi < activeTop - eps) {
              sampleGap = Math.min(sampleGap, activeTop - yi)
            } else if (Math.abs(yi - activeTop) <= boundaryEps) {
              sampleGap = Math.min(sampleGap, 0)
            }
          }
          if (!Number.isFinite(sampleGap)) {
            sampleGap = resolveFlushGapWhenRayMissed(
              { x: x0, y: activeTop },
              { x: x0, y: activeTop - probeStep }
            )
          }
          appendSampleGap(sampleGap)
        }
      } else if (direction === 'down') {
        for (let si = 0; si < xSamples.length; ++si) {
          const x0 = xSamples[si]
          let sampleGap = Infinity
          for (let i = 0; i < n; ++i) {
            const yi = intersectVertical(x0, pts[i], pts[(i + 1) % n])
            if (yi == null) continue
            if (yi > activeBottom + eps) {
              sampleGap = Math.min(sampleGap, yi - activeBottom)
            } else if (Math.abs(yi - activeBottom) <= boundaryEps) {
              sampleGap = Math.min(sampleGap, 0)
            }
          }
          if (!Number.isFinite(sampleGap)) {
            sampleGap = resolveFlushGapWhenRayMissed(
              { x: x0, y: activeBottom },
              { x: x0, y: activeBottom + probeStep }
            )
          }
          appendSampleGap(sampleGap)
        }
      }

      return Number.isFinite(d) && d !== Infinity ? d : Infinity
    },

    findActiveNestHoleContext(ap, filteredParts) {
      let best = null
      let bestArea = Infinity

      for (let i = 0; i < filteredParts.length; ++i) {
        const parent = filteredParts[i]
        if (isSamePart(parent, ap) || this.isLikelySamePlank(parent, ap)) {
          continue
        }

        const pathLen = parent.path?.length || 0
        for (let hi = 1; hi < pathLen; hi++) {
          const holePoly = this.getHolePolygonAbs(parent, hi)
          if (!holePoly?.length) continue
          if (!this.isPartInsideHolePolygon(holePoly, ap)) continue
          const hb = this.getPolygonAabb(holePoly)
          if (!hb) continue
          const area = (hb.maxX - hb.minX) * (hb.maxY - hb.minY)
          if (area < bestArea) {
            bestArea = area
            best = { parent, holePoly }
          }
        }

        const curvePenCount =
          parent._curveHolesPoly?.length ||
          (parent.curveHoles || []).filter((ch) =>
            compareNumbers(ch.deep, parent.thick)
          ).length
        for (let pi = 0; pi < curvePenCount; pi++) {
          const holePoly = this.getCurveHolePolygonAbs(parent, pi)
          if (!holePoly?.length) continue
          if (!this.isPartInsideHolePolygon(holePoly, ap)) continue
          const hb = this.getPolygonAabb(holePoly)
          if (!hb) continue
          const area = (hb.maxX - hb.minX) * (hb.maxY - hb.minY)
          if (area < bestArea) {
            bestArea = area
            best = { parent, holePoly }
          }
        }
      }
      return best
    },

    isPartNestedInAnyHoleOf(outer, inner) {
      if (isSamePart(outer, inner)) return false
      const pathLen = outer.path?.length || 0
      for (let hi = 1; hi < pathLen; hi++) {
        const hp = this.getHolePolygonAbs(outer, hi)
        if (hp && this.isPartInsideHolePolygon(hp, inner)) return true
      }
      const curvePenCount =
        outer._curveHolesPoly?.length ||
        (outer.curveHoles || []).filter((ch) =>
          compareNumbers(ch.deep, outer.thick)
        ).length
      for (let pi = 0; pi < curvePenCount; pi++) {
        const hp = this.getCurveHolePolygonAbs(outer, pi)
        if (hp && this.isPartInsideHolePolygon(hp, inner)) return true
      }
      return false
    },

    getKeyboardMoveDelta(direction, distance) {
      const d = Number(distance) || 0
      if (!d) return { dx: 0, dy: 0 }
      if (direction === 'up') return { dx: 0, dy: -d }
      if (direction === 'down') return { dx: 0, dy: d }
      if (direction === 'left') return { dx: -d, dy: 0 }
      if (direction === 'right') return { dx: d, dy: 0 }
      return { dx: 0, dy: 0 }
    },

    /**
     * 键盘几何净空需再预留的量（与 checkOnePlank 对齐）。
     * checkOne 量到的间隙 = 几何缝 + 锯片/刀径(+扩版)；required 已含锯片/刀径。
     * 故对几何净空只需预留 required - 锯片/刀径，不能整段扣 required，否则会把可移动量扣成 0。
     */
    getKeyboardReserveGap(ap, bigpart, direction) {
      let layoutGap = Number(this.ncSetting.panelSize.layoutGap) || 0
      layoutGap = layoutGap === 0 ? 0.01 : layoutGap
      const edgeDir = KEYBOARD_DIR_TO_EDGE[direction]
      if (!ap || !bigpart || !edgeDir) return layoutGap

      const stockKey = ap.stockKey || bigpart.stockKey
      const diameter = Number(getPlateKnifeDiameter(stockKey, this.ncSetting)) || 0
      const sawDiameter = Number(getSawKnife(this.ncSetting)) || 0
      // checkOne 量间隙时加在几何缝上的刀径
      const toolInMeasured = ap.isUseSaw ? sawDiameter : diameter
      const toGeoReserve = (required) =>
        Math.max(Number(required) - toolInMeasured, layoutGap)

      const range = 50 + (ap.isUseSaw ? sawDiameter : diameter)
      const nearestMap = getNearestPartsInRange(ap, bigpart, range)
      const neighbor = nearestMap?.[edgeDir]
      if (!neighbor) {
        // 邻板扫描漏检时：按异形最大 required 估算，再扣回已计入量测的刀径
        if (ap.isUseSaw) {
          return toGeoReserve(Math.max(diameter, sawDiameter) + 0.5)
        }
        return toGeoReserve(diameter || 0)
      }

      const required = getAdjacentEdgeRequiredGap(
        ap,
        neighbor,
        edgeDir,
        diameter,
        sawDiameter
      )
      if (!Number.isFinite(required) || required < 0) return layoutGap
      return toGeoReserve(required)
    },

    calcKeyboardMoveDistance(ap, bigpart, filteredParts, direction) {
      const stepCap = this.getKeyboardStepCap()
      const isOuterShell = this.isOuterNestShell(ap, filteredParts)
      const clearBk = this.getRectCenterStripClearanceBreakdown(
        ap,
        bigpart,
        filteredParts,
        direction,
        { includeFixedHoleChildren: isOuterShell }
      )
      const rawClearance =
        clearBk && Number.isFinite(clearBk.final) ? clearBk.final : null
      const limitIsBigBoardOuterOnly =
        this.isClearanceLimitedByBigBoardOuter(clearBk)
      let distance = 0
      if (
        rawClearance != null &&
        Number.isFinite(rawClearance) &&
        compareTowNum(rawClearance, 0, '>')
      ) {
        if (limitIsBigBoardOuterOnly) {
          const knifeRadius =
            Number(getPlateKnifeDiameter(bigpart.stockKey, this.ncSetting)) /
              2 || 0
          if (knifeRadius > 0 && compareTowNum(rawClearance, stepCap, '<')) {
            distance = rawClearance + knifeRadius
          } else {
            distance = rawClearance
            if (compareTowNum(distance, stepCap, '>=')) {
              distance = stepCap
            }
          }
          if (distance < 0) distance = 0
        } else {
          const reserveGap = this.getKeyboardReserveGap(ap, bigpart, direction)
          if (compareTowNum(rawClearance, reserveGap, '>=')) {
            distance = rawClearance - reserveGap
            if (distance < 0) distance = 0
            if (compareTowNum(distance, 0, '>')) {
              if (compareTowNum(distance, stepCap, '>=')) {
                distance = stepCap
              }
            } else {
              distance = 0
            }
          }
        }
      }
      return distance
    },

    /**
     * 仅移动 activePlank；移动后用 checkOnePlank 校验，
     * 冲突则回退到移动前，无冲突允许移动（满步长失败时二分找最大安全位移）
     */
    applyKeyboardPlankMove(
      activePlank,
      bigpart,
      direction,
      maxDistance,
      origStartX,
      origStartY
    ) {
      const cap = Number(maxDistance) || 0

      function restoreOrigin() {
        activePlank.startX = origStartX
        activePlank.startY = origStartY
        dealPlankPoly(activePlank)
        activePlank.plankMerge = !checkOnePlank(activePlank, bigpart)
      }

      if (!activePlank || !cap || !compareTowNum(cap, 0, '>')) {
        restoreOrigin()
        return 0
      }

      /** @returns {boolean} true=无冲突可停留 */
      const tryAt = (d) => {
        const { dx, dy } = this.getKeyboardMoveDelta(direction, d)
        activePlank.startX = origStartX + dx
        activePlank.startY = origStartY + dy
        dealPlankPoly(activePlank)
        const ok = checkOnePlank(activePlank, bigpart)
        activePlank.plankMerge = !ok
        return ok
      }

      if (tryAt(cap)) return cap

      let low = 0
      let high = cap
      let best = 0
      for (let i = 0; i < 24; ++i) {
        const mid = (low + high) / 2
        if (tryAt(mid)) {
          best = mid
          low = mid
        } else {
          high = mid
        }
        if (high - low < 0.01) break
      }

      if (!best || !tryAt(best)) {
        restoreOrigin()
        return 0
      }
      return best
    },

    updateEdgeCenterDistances() {
      if (!this.$props.isShowMoveStep || !this.activePlank) {
        this.edgeCenterDistances = null
        return
      }
      const bigpart = this.findBigpartForEdgeOverlay()
      if (!bigpart) {
        this.edgeCenterDistances = null
        return
      }
      const filteredParts = bigpart.parts.filter((p) => !p.blueAreaSurplus)
      const ap = this.resolveLiveActivePart(bigpart)
      if (ap && ap !== this.activePlank) {
        ap.startX = this.activePlank.startX
        ap.startY = this.activePlank.startY
      }
      const measureAp = ap || this.activePlank
      if (this.isPartInNestHoleInterior(measureAp, filteredParts)) {
        this.edgeCenterDistances = null
        return
      }
      if (this.isPartInExternalIrregularNestCluster(measureAp, filteredParts)) {
        this.edgeCenterDistances = null
        return
      }
      const dirs = ['up', 'down', 'left', 'right']
      const result = {}
      for (let i = 0; i < dirs.length; ++i) {
        const direction = dirs[i]
        result[direction] = this.calcRectCenterStripClearance(
          measureAp,
          bigpart,
          filteredParts,
          direction
        )
      }
      this.edgeCenterDistances = result
    },

    getRectCenterStripClearanceBreakdown(
      ap,
      bigpart,
      filteredParts,
      direction,
      options = {}
    ) {
      const { includeFixedHoleChildren = false } = options
      const eps = 0.5
      const activeArr = this.getActiveOutlineArr(ap)
      const { activeLeft, activeRight, activeTop, activeBottom } = activeArr
      const cx = (activeLeft + activeRight) / 2
      const cy = (activeTop + activeBottom) / 2
      const w = activeRight - activeLeft
      const h = activeBottom - activeTop
      const nest = this.findActiveNestHoleContext(ap, filteredParts)
      const isOuterShell = this.isOuterNestShell(ap, filteredParts)
      let partsToCheck = filteredParts
      let excludeHoleChildrenOnly = false
      if (nest && !isOuterShell) {
        partsToCheck = filteredParts.filter(
          (p) =>
            !isSamePart(p, ap) &&
            !isSamePart(p, nest.parent) &&
            this.isPartInsideHolePolygon(nest.holePoly, p)
        )
      } else if (isOuterShell) {
        if (includeFixedHoleChildren) {
          // 键盘移动：孔内子板固定，作为外层板位移障碍参与净空
          partsToCheck = filteredParts.filter((p) => !isSamePart(p, ap))
          excludeHoleChildrenOnly = false
        } else {
          partsToCheck = filteredParts.filter(
            (p) => !isSamePart(p, ap) && !this.isPartNestedInAnyHoleOf(ap, p)
          )
          excludeHoleChildrenOnly = true
        }
      }
      const bboxPath = [
        { x: 0, y: 0 },
        { x: w, y: 0 },
        { x: w, y: h },
        { x: 0, y: h },
      ]
      let distPlank = Infinity

      for (let i = 0; i < partsToCheck.length; ++i) {
        const part = partsToCheck[i]
        if (isSamePart(part, ap)) continue
        const partArr = {
          partLeft: part.startX,
          partRight: part.startX + part.rect.width,
          partTop: part.startY,
          partBottom: part.startY + part.rect.height,
        }
        const { partLeft, partRight, partTop, partBottom } = partArr

        let stripOverlap = false
        if (direction === 'up' || direction === 'down') {
          const maxN = Math.max(partLeft, cx - eps)
          const minN = Math.min(partRight, cx + eps)
          stripOverlap = maxN < minN && Math.abs(maxN - minN) > 0.0001
        } else {
          const maxN = Math.max(partTop, cy - eps)
          const minN = Math.min(partBottom, cy + eps)
          stripOverlap = maxN < minN && Math.abs(maxN - minN) > 0.0001
        }
        if (!stripOverlap) continue
        if (
          !this.isPartBlockingInDirection(direction, activeArr, partArr, eps)
        ) {
          continue
        }

        const aabbDist = this.getPartAabbStripClearance(
          direction,
          activeArr,
          part,
          eps
        )
        if (Number.isFinite(aabbDist) && compareTowNum(aabbDist, 0, '>=')) {
          distPlank = Math.min(distPlank, aabbDist)
        }

        if (!part.path && !ap.path) {
          if (direction === 'up') {
            if (activeArr.activeBottom < partTop) continue
            const distance = activeArr.activeTop - partBottom
            if (compareTowNum(distance, 0, '>=')) {
              distPlank = Math.min(distPlank, distance)
            }
          } else if (direction === 'down') {
            if (partBottom < activeArr.activeTop) continue
            const distance = partTop - activeArr.activeBottom
            if (compareTowNum(distance, 0, '>=')) {
              distPlank = Math.min(distPlank, distance)
            }
          } else if (direction === 'left') {
            if (activeArr.activeLeft < partRight) continue
            const distance = activeArr.activeLeft - partRight
            if (compareTowNum(distance, 0, '>=')) {
              distPlank = Math.min(distPlank, distance)
            }
          } else if (direction === 'right') {
            if (activeArr.activeRight > partLeft) continue
            const distance = partLeft - activeArr.activeRight
            if (compareTowNum(distance, 0, '>=')) {
              distPlank = Math.min(distPlank, distance)
            }
          }
        } else {
          const result = this.judgeDirectionLine(
            activeArr,
            partArr,
            bboxPath,
            ap,
            part,
            direction,
            true
          )
          if (result.data && result.data.length) {
            const plankDistance = Math.min(...result.data)
            if (compareTowNum(plankDistance, 0, '>=')) {
              distPlank = Math.min(distPlank, plankDistance)
            }
          }
        }

        // judgeDirectionLine 仅取垂直/水平边，异形相邻板（如梯形斜边）会漏判
        const polyDist = this.getPartPolygonStripClearance(
          direction,
          activeArr,
          part
        )
        if (Number.isFinite(polyDist) && compareTowNum(polyDist, 0, '>=')) {
          distPlank = Math.min(distPlank, polyDist)
        }
      }

      // 孔洞内子板：须用孔洞轮廓限制步长，否则大步长会按大板外缘算出过大位移导致碰撞回滚
      let distHole = Infinity
      if (nest && !isOuterShell && nest.holePoly?.length) {
        distHole = this.getHolePolygonAxisClearance(
          direction,
          activeArr,
          nest.holePoly
        )
        if (!Number.isFinite(distHole)) distHole = Infinity
      }

      let distBig = this.getBigPlankAxisClearance(bigpart, direction, activeArr)
      if (!Number.isFinite(distBig)) distBig = Infinity
      // 台面宽度限制：按 xyReverse 约束 X 或 Y 方向净空
      const plankWidth =
        Number(bigpart.plankWidth) || Number(bigpart.surplusInfo?.width) || 0
      const plankHeight =
        Number(bigpart.plankHeight) ||
        Number(bigpart.surplusInfo?.height) ||
        Number(bigpart.surplusInfo?.long) ||
        0
      const counterClearance = getCounterAxisClearance(
        direction,
        activeArr,
        { plankWidth, plankHeight, bigpart },
        this.ncSetting
      )
      if (Number.isFinite(counterClearance)) {
        distBig = Math.min(distBig, Math.max(0, counterClearance))
      }
      let final = Math.min(distPlank, distBig)
      if (Number.isFinite(distHole) && distHole !== Infinity) {
        final = Math.min(final, distHole)
      }
      if (!Number.isFinite(final) || final === Infinity) {
        return {
          distPlank,
          distBig,
          distHole,
          final: Infinity,
          nest,
          excludeHoleChildrenOnly,
        }
      }
      return {
        distPlank,
        distBig,
        distHole,
        final,
        nest,
        excludeHoleChildrenOnly,
      }
    },

    isClearanceLimitedByBigBoardOuter(b) {
      if (!b || b.nest) return false
      if (!Number.isFinite(b.distBig)) return false
      const dp = b.distPlank
      if (!Number.isFinite(dp) || dp === Infinity) {
        // 带孔外层板仅受大板 shrink 限制时，全程按大板贴边逻辑（含刀半径最后一跳）
        if (b.excludeHoleChildrenOnly) return true
        return true
      }
      return compareTowNum(b.distBig, dp + 1e-6, '<=')
    },

    calcRectCenterStripClearance(ap, bigpart, filteredParts, direction) {
      const b = this.getRectCenterStripClearanceBreakdown(
        ap,
        bigpart,
        filteredParts,
        direction
      )
      if (!b || !Number.isFinite(b.final) || b.final === Infinity) return null
      let out = b.final
      const layoutGap = Number(this.ncSetting.panelSize.layoutGap) || 0
      const boundByBigBoardOnly = this.isClearanceLimitedByBigBoardOuter(b)
      if (
        boundByBigBoardOnly &&
        layoutGap > 0 &&
        compareTowNum(out, layoutGap, '<=')
      ) {
        out = 0
      }
      return +out.toFixed(2)
    },

    genPartsRect(part, bigPankStartX, bigPankStartY) {
      return {
        x: bigPankStartX + this.tranlateSizeFromScale(part.startX),
        y: bigPankStartY + this.tranlateSizeFromScale(part.startY),
        width: this.tranlateSizeFromScale(part.rect.width),
        height: this.tranlateSizeFromScale(part.rect.height),
      }
    },

    getOutlineAnchorCanvas(ap, newLeft, newTop, direction) {
      const outline = this.getActiveOutlineArr(ap)
      const leftCanvas =
        newLeft + this.tranlateSizeFromScale(outline.activeLeft)
      const rightCanvas =
        newLeft + this.tranlateSizeFromScale(outline.activeRight)
      const topCanvas = newTop + this.tranlateSizeFromScale(outline.activeTop)
      const bottomCanvas =
        newTop + this.tranlateSizeFromScale(outline.activeBottom)
      const cxCanvas = (leftCanvas + rightCanvas) / 2
      const cyCanvas = (topCanvas + bottomCanvas) / 2

      if (direction === 'up') return { x: cxCanvas, y: topCanvas }
      if (direction === 'down') return { x: cxCanvas, y: bottomCanvas }
      if (direction === 'left') return { x: leftCanvas, y: cyCanvas }
      return { x: rightCanvas, y: cyCanvas }
    },

    drawEdgeCenterOverlayLast() {
      const o = this.edgeOverlayCanvasOrigin
      if (
        !this.$props.isShowMoveStep ||
        !this.edgeCenterDistances ||
        !this.activePlank ||
        !o ||
        o.stockKey !== this.activePlank.stockKey
      ) {
        return
      }
      this.drawEdgeCenterDistanceOverlay(this.ctx, o.newLeft, o.newTop)
    },

    drawEdgeCenterDistanceOverlay(ctx, newLeft, newTop) {
      const d = this.edgeCenterDistances
      if (!d || !this.activePlank) return
      const bigpart = this.findBigpartForEdgeOverlay()
      if (!bigpart) return
      const ap = this.resolveLiveActivePart(bigpart)
      if (!ap) return
      const filteredParts = bigpart.parts.filter((p) => !p.blueAreaSurplus)
      if (this.isPartInNestHoleInterior(ap, filteredParts)) return
      if (this.isPartInExternalIrregularNestCluster(ap, filteredParts)) return
      const upA = this.getOutlineAnchorCanvas(ap, newLeft, newTop, 'up')
      const downA = this.getOutlineAnchorCanvas(ap, newLeft, newTop, 'down')
      const leftA = this.getOutlineAnchorCanvas(ap, newLeft, newTop, 'left')
      const rightA = this.getOutlineAnchorCanvas(ap, newLeft, newTop, 'right')
      const fmt = (v) => (v == null ? '' : String(v))
      const themeColor = '#1890ff'

      ctx.save()
      ctx.strokeStyle = themeColor
      ctx.fillStyle = themeColor
      ctx.setLineDash([])
      ctx.lineWidth = Math.max(1, this.scalePercent)

      function strokeSeg(x1, y1, x2, y2) {
        ctx.beginPath()
        ctx.moveTo(x1, y1)
        ctx.lineTo(x2, y2)
        ctx.stroke()
      }

      if (d.up != null && compareTowNum(d.up, 0, '>')) {
        const len = this.tranlateSizeFromScale(d.up)
        strokeSeg(upA.x, upA.y, upA.x, upA.y - len)
      }
      if (d.down != null && compareTowNum(d.down, 0, '>')) {
        const len = this.tranlateSizeFromScale(d.down)
        strokeSeg(downA.x, downA.y, downA.x, downA.y + len)
      }
      if (d.left != null && compareTowNum(d.left, 0, '>')) {
        const len = this.tranlateSizeFromScale(d.left)
        strokeSeg(leftA.x, leftA.y, leftA.x - len, leftA.y)
      }
      if (d.right != null && compareTowNum(d.right, 0, '>')) {
        const len = this.tranlateSizeFromScale(d.right)
        strokeSeg(rightA.x, rightA.y, rightA.x + len, rightA.y)
      }

      ctx.font = '12px PingFangSC-Regular, PingFang SC'
      const labelShift = Math.max(14, 12 * this.scalePercent)

      if (d.up != null) {
        if (compareTowNum(d.up, 0, '>')) {
          const len = this.tranlateSizeFromScale(d.up)
          ctx.textAlign = 'left'
          ctx.textBaseline = 'middle'
          ctx.fillText(fmt(d.up), upA.x + labelShift, upA.y - len / 2)
        } else {
          ctx.textAlign = 'left'
          ctx.textBaseline = 'bottom'
          ctx.fillText(fmt(d.up), upA.x + labelShift, upA.y - 4)
        }
      }
      if (d.down != null) {
        if (compareTowNum(d.down, 0, '>')) {
          const len = this.tranlateSizeFromScale(d.down)
          ctx.textAlign = 'left'
          ctx.textBaseline = 'middle'
          ctx.fillText(fmt(d.down), downA.x + labelShift, downA.y + len / 2)
        } else {
          ctx.textAlign = 'left'
          ctx.textBaseline = 'top'
          ctx.fillText(fmt(d.down), downA.x + labelShift, downA.y + 4)
        }
      }
      if (d.left != null) {
        if (compareTowNum(d.left, 0, '>')) {
          const len = this.tranlateSizeFromScale(d.left)
          ctx.textAlign = 'center'
          ctx.textBaseline = 'middle'
          ctx.fillText(fmt(d.left), leftA.x - len / 2, leftA.y - labelShift)
        } else {
          ctx.textAlign = 'right'
          ctx.textBaseline = 'middle'
          ctx.fillText(fmt(d.left), leftA.x - 4, leftA.y - labelShift)
        }
      }
      if (d.right != null) {
        if (compareTowNum(d.right, 0, '>')) {
          const len = this.tranlateSizeFromScale(d.right)
          ctx.textAlign = 'center'
          ctx.textBaseline = 'middle'
          ctx.fillText(fmt(d.right), rightA.x + len / 2, rightA.y - labelShift)
        } else {
          ctx.textAlign = 'left'
          ctx.textBaseline = 'middle'
          ctx.fillText(fmt(d.right), rightA.x + 4, rightA.y - labelShift)
        }
      }
      ctx.restore()
    },
  },
}
